import { useEffect, useRef } from 'react';
import { init, use, type ComposeOption, type ECharts } from 'echarts/core';
import { BarChart, LineChart, type BarSeriesOption, type LineSeriesOption } from 'echarts/charts';
import { GridComponent, TooltipComponent, type GridComponentOption, type TooltipComponentOption } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import type { AccountGroupKey, BalanceHistory } from '../api.js';
import { needsInit } from '../components/MetricChart.js';
import { useThemeMode } from '../lib/useThemeMode.js';
import { dollars, monthLabel, shortDate } from './format.js';

use([BarChart, LineChart, GridComponent, TooltipComponent, CanvasRenderer]);

type ChartOption = ComposeOption<BarSeriesOption | LineSeriesOption | GridComponentOption | TooltipComponentOption>;

/**
 * Same soft family as the cash-flow Sankey: big filled areas in saturated hues
 * read as harsh. Assets in cool/warm pastels, debts in rose, net worth as the
 * one strong line on top.
 */
export const GROUP_COLORS: Record<'light' | 'dark', Record<AccountGroupKey, string>> = {
  light: { cash: '#6f9ef5', investment: '#a68ef0', vehicle: '#f4a462', property: '#55c2d8', credit: '#ec7f8c', loan: '#e78fc9' },
  dark: { cash: '#5b88dc', investment: '#8d76d9', vehicle: '#d98c4f', property: '#46a8bd', credit: '#d9707c', loan: '#cc78b0' },
};

const CHROME = {
  light: { line: '#1c1c1e', text: 'rgba(60,60,67,0.72)', grid: 'rgba(60,60,67,0.12)', tipBg: '#ffffff', tipText: '#1c1c1e' },
  dark: { line: '#f5f5f7', text: 'rgba(235,235,245,0.68)', grid: 'rgba(84,84,88,0.45)', tipBg: '#2c2c2e', tipText: '#f5f5f7' },
} as const;

/** "$12k", "$1.2M" for the axis; the tooltip carries the exact figure. */
function compact(dollarsValue: number): string {
  const a = Math.abs(dollarsValue);
  const sign = dollarsValue < 0 ? '−' : '';
  if (a >= 1_000_000) return `${sign}$${(a / 1_000_000).toFixed(a >= 10_000_000 ? 0 : 1)}M`;
  if (a >= 1_000) return `${sign}$${Math.round(a / 1_000)}k`;
  return `${sign}$${Math.round(a)}`;
}

function bucketLabel(b: string, interval: BalanceHistory['interval']): string {
  return interval === 'month' ? monthLabel(b.slice(0, 7)) : shortDate(b);
}

function buildOption(h: BalanceHistory, mode: 'light' | 'dark'): ChartOption {
  const c = CHROME[mode];
  const colors = GROUP_COLORS[mode];
  const labels = h.buckets.map((b) => bucketLabel(b, h.interval));
  const bars: BarSeriesOption[] = h.groups.map((g) => ({
    type: 'bar',
    name: g.label,
    stack: g.liability ? 'owed' : 'owned',
    // Debts hang below the axis so the bar's height above minus below is the net.
    data: g.values.map((v) => (g.liability ? -v : v) / 100),
    itemStyle: { color: colors[g.key] ?? '#8ea4c2', opacity: 0.55, borderRadius: 2 },
    emphasis: { itemStyle: { opacity: 0.85 } },
    barCategoryGap: '30%',
  }));
  const net: LineSeriesOption = {
    type: 'line',
    name: 'Net worth',
    data: h.net.map((v) => v / 100),
    smooth: 0.25,
    symbol: 'circle',
    symbolSize: 5,
    showSymbol: h.buckets.length <= 14,
    lineStyle: { width: 2.5, color: c.line },
    itemStyle: { color: c.line },
    z: 5,
  };
  return {
    grid: { left: 4, right: 8, top: 12, bottom: 4, containLabel: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: c.tipBg,
      borderWidth: 0,
      textStyle: { color: c.tipText, fontSize: 12 },
      formatter: (raw: unknown) => {
        const ps = raw as Array<{ seriesName: string; value: number; color: string; dataIndex: number }>;
        if (!ps.length) return '';
        const i = ps[0]!.dataIndex;
        const rows = ps
          .filter((p) => p.seriesName !== 'Net worth' && p.value !== 0)
          .map((p) => `<span style="color:${p.color}">●</span> ${p.seriesName} ${dollars(Math.abs(p.value) * 100)}`);
        return [`<b>${labels[i]}</b>`, `Net worth <b>${dollars(h.net[i] ?? 0)}</b>`, ...rows].join('<br/>');
      },
    },
    xAxis: {
      type: 'category',
      data: labels,
      axisLine: { lineStyle: { color: c.grid } },
      axisTick: { show: false },
      axisLabel: { color: c.text, fontSize: 11, hideOverlap: true },
    },
    yAxis: {
      type: 'value',
      splitNumber: 3,
      axisLabel: { color: c.text, fontSize: 11, formatter: (v: number) => compact(v) },
      splitLine: { lineStyle: { color: c.grid } },
    },
    series: [...bars, net],
  };
}

/** Net worth over time (Monarch "Accounts" chart): bars per account group, net worth as a line. */
export default function NetWorthChart({ history }: { history: BalanceHistory }): JSX.Element {
  const mode = useThemeMode();
  const elRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ECharts | null>(null);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    if (needsInit(chartRef.current, el)) {
      chartRef.current?.dispose();
      chartRef.current = init(el);
    }
    chartRef.current?.setOption(buildOption(history, mode), true);
    const ro = new ResizeObserver(() => chartRef.current?.resize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [history, mode]);

  useEffect(() => {
    return () => {
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []);

  const last = history.net[history.net.length - 1];
  return (
    <div
      ref={elRef}
      className="mn-nwchart"
      role="img"
      aria-label={`Net worth over time${last != null ? `, now ${dollars(last)}` : ''}`}
    />
  );
}
